import { Box } from "@mui/material";
import React from "react";
import { Divider, Chip, Button } from "@mui/material";
import {
  ArrayField,
  BooleanField,
  ChipField,
  Datagrid,
  DateField,
  EditButton,
  Labeled,
  RichTextField,
  Show,
  ShowViewProps,
  SingleFieldList,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useRecordContext,
  useNotify,
  useShowController,
  Link,
} from "react-admin";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import { API_URL } from "../../../constants";
import { FieldProps, RaRecord } from "react-admin";
import { getAccessToken } from "../../../utils/storage";

interface UserRecord {
  applicantProfile?: {
    extraSkills?: string[];
    id?: number;
    cv?: string;
  };
  fullName?: string;
  role?: string;
}

interface ApplicationRecord extends RaRecord {
  offer?: {
    id?: number;
    reference?: string;
  };
}

const TagsField = () => {
  const record = useRecordContext<UserRecord>();
  if (!record?.applicantProfile?.extraSkills) {
    return null;
  }
  return (
    <ul style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
      {record.applicantProfile.extraSkills.map((item: string, index: number) => (
        <Chip key={index} label={item} />
      ))}
    </ul>
  );
};

const OfferURLField = (props: FieldProps) => {
  const record = useRecordContext<ApplicationRecord>(props);
  if (!record?.offer?.id) {
    return null;
  }
  return (
    <Link to={`/job-offers/${record.offer.id}/show`}>
      {record.offer.reference || "N/A"}
    </Link>
  );
};

const CustomActions = () => {
  const record = useRecordContext<UserRecord>();
  return (
    <TopToolbar>
      {record && (
        <Link
          to={`/job-applications?filter={"fullName"%3A"${record.fullName}"}`}
        >
          <HowToVoteIcon />
          Ver postulaciones
        </Link>
      )}
      <EditButton />
    </TopToolbar>
  );
};

const UserTitle = () => {
  const record = useRecordContext<UserRecord>();
  if (!record) return null;
  return <>{record.fullName || ""}</>;
};

export const ViewUser: React.FC<ShowViewProps> = (props) => {
  const { record, isLoading } = useShowController<UserRecord>(props);
  const notify = useNotify();

  const downloadCV = React.useCallback(async () => {
    try {
      const profileId = record?.applicantProfile?.id;
      if (!profileId) {
        throw new Error("Profile ID not found");
      }

      const apiUrl = `${API_URL}/applicant-profiles/${profileId}/cv`;

      const token = getAccessToken();
      const headers: HeadersInit = { Accept: "application/json" };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to fetch CV URL");
      }

      const data = await response.json();
      const url = data?.url;
      if (!url) {
        throw new Error("CV URL not found in response");
      }

      // Validar que la URL sea segura antes de abrirla
      try {
        const urlObj = new URL(url);
        if (!["http:", "https:"].includes(urlObj.protocol)) {
          throw new Error("URL no segura");
        }
        window.open(url, "_blank", "noopener,noreferrer");
      } catch (urlError) {
        throw new Error("URL inválida o no segura");
      }
    } catch (error) {
      notify(
        error instanceof Error
          ? `Error al descargar CV: ${error.message}`
          : "Error al descargar CV",
        { type: "error" }
      );
    }
  }, [record?.applicantProfile?.id, notify]);

  if (isLoading || !record) {
    return <Box>Cargando...</Box>;
  }

  if (!record.applicantProfile && record.role !== "ADMIN") {
    return <Box>No se encontró el perfil del solicitante</Box>;
  }

  return (
    <Show {...props} title={<UserTitle />} actions={<CustomActions />}>
      <TabbedShowLayout spacing={2} divider={<Divider flexItem />}>
        <TabbedShowLayout.Tab label="Información Personal" spacing={100}>
          <TextField source="email" label="Correo Electrónico" />
          <TextField source="role" label="Rol" />
          {record.role !== "ADMIN" ? (
            <Box
              display="flex"
              flexDirection="column"
              gap={2}
              displayPrint="none"
            >
              <Labeled>
                <DateField
                  source="applicantProfile.birthday"
                  label="Fecha de Nacimiento"
                />
              </Labeled>
              <Labeled>
                <TextField source="applicantProfile.gender" label="Género" />
              </Labeled>
              <Labeled>
                <TextField
                  source="applicantProfile.linkedInProfile"
                  label="LinkedIn"
                  emptyText="N/A"
                />
              </Labeled>
              <Labeled>
                <TextField source="applicantProfile.phone" label="Teléfono" />
              </Labeled>
              <Labeled>
                <TextField
                  source="applicantProfile.address1"
                  label="Dirección 1"
                />
              </Labeled>
              <Labeled>
                <TextField
                  source="applicantProfile.address2"
                  label="Dirección 2"
                />
              </Labeled>
              <Labeled>
                {record?.applicantProfile?.cv ? (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={downloadCV}
                  >
                    Descargar CV
                  </Button>
                ) : (
                  <TextField
                    source="applicantProfile.cv"
                    label="CV"
                    emptyText="N/A"
                  />
                )}
              </Labeled>
              <Labeled>
                <TextField
                  source="applicantProfile.title"
                  label="Título"
                  emptyText="N/A"
                />
              </Labeled>
            </Box>
          ) : null}
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Historial Laboral"
          disabled={record.role === "ADMIN"}
        >
          <RichTextField
            source="applicantProfile.workExperience.description"
            label="Descripción del Trabajo"
            emptyText="N/A"
          />
          <TextField
            source="applicantProfile.workExperience.position"
            label="Puesto de Trabajo"
            emptyText="N/A"
          />
          <TextField
            source="applicantProfile.workExperience.company"
            label="Compañía"
            emptyText="N/A"
          />
          <DateField
            source="applicantProfile.workExperience.startDate"
            label="Fecha de Inicio"
            emptyText="N/A"
          />
          <DateField
            source="applicantProfile.workExperience.endDate"
            label="Fecha de Fin"
            emptyText="N/A"
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Intereses"
          disabled={record.role === "ADMIN"}
        >
          <ArrayField source="applicantProfile.interests" label="Intereses">
            <SingleFieldList>
              <ChipField source="name" />
            </SingleFieldList>
          </ArrayField>
          <BooleanField
            source="applicantProfile.receiveEmails"
            label="Recibir Correos Electrónicos"
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Idiomas"
          disabled={record.role === "ADMIN"}
        >
          <ArrayField source="applicantProfile.languages" label="Idiomas">
            <Datagrid bulkActionButtons={false}>
              <TextField source="name" label="Idioma" />
              <TextField source="level" label="Nivel" />
              <RichTextField source="comments" label="Comentarios" />
            </Datagrid>
          </ArrayField>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Educación"
          disabled={record.role === "ADMIN"}
        >
          <TextField
            source="applicantProfile.education.degreeLevel.name"
            label="Nivel"
            emptyText="N/A"
          />
          <RichTextField
            source="applicantProfile.education.title"
            label="Título"
            emptyText="N/A"
          />
          <TextField
            source="applicantProfile.education.institution"
            label="Institución"
            emptyText="N/A"
          />
          <DateField
            source="applicantProfile.education.dateObtained"
            label="Fecha de Obtención"
            emptyText="N/A"
          />
          <RichTextField
            source="applicantProfile.education.comments"
            label="Comentarios"
            emptyText="N/A"
          />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Habilidades"
          disabled={record.role === "ADMIN"}
        >
          <TagsField />
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab
          label="Postulaciones"
          disabled={record.role === "ADMIN"}
        >
          <ArrayField
            source="applicantProfile.applications"
            label="Postulaciones"
          >
            <Datagrid bulkActionButtons={false}>
              <OfferURLField label="Oferta" />
              <TextField source="offer.title" label="Puesto" />
              <DateField source="createdAt" label="Postulado En" />
            </Datagrid>
          </ArrayField>
        </TabbedShowLayout.Tab>
        <TabbedShowLayout.Tab label="Auditoría">
          <DateField source="createdAt" label="Creado En" />
          <DateField source="updatedAt" label="Actualizado En" />
        </TabbedShowLayout.Tab>
      </TabbedShowLayout>
    </Show>
  );
};
